import React, { useState, useEffect } from 'react';
import { phoneAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

const Admin = () => {
  const [phones, setPhones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    marca: '', modelo: '', imei: '', serial: '', status: '', user: '',
    condicion: '', comentarios: '', equipo_asociado: '', ubicacion: '',
    precio_base: '', estrellas: 3, grado: 'B', sistema_operativo: 'Android',
    numero_activo: '', vendido: false
  });

  useEffect(() => {
    if (!isAdmin()) { navigate('/'); return; }
    loadPhones();
  }, []);

  const loadPhones = async () => {
    try {
      const response = await phoneAPI.getAll();
      setPhones(response.data);
    } catch (error) {
      console.error('Error loading phones:', error);
      alert('Error al cargar los teléfonos');
    }
  };

  const handlePhotoCapture = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newPhotos = Array.from(e.target.files);
      setSelectedPhotos(prev => [...prev, ...newPhotos]);
      alert(`✅ ${newPhotos.length} foto(s) agregada(s)`);
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let phoneId;
      const submitData = { ...formData, vendido: formData.vendido ? 1 : 0 };
      
      if (editingPhone) {
        await phoneAPI.update(editingPhone.id, submitData);
        phoneId = editingPhone.id;
        alert('Teléfono actualizado exitosamente');
      } else {
        const response = await phoneAPI.create(submitData);
        phoneId = response.data.phoneId;
        alert('Teléfono creado exitosamente');
      }
      
      if (selectedPhotos.length > 0 && phoneId) {
        const formDataImages = new FormData();
        selectedPhotos.forEach(file => { formDataImages.append('images', file); });
        await phoneAPI.uploadImages(phoneId, formDataImages);
      }
      resetForm();
      loadPhones();
    } catch (error) {
      console.error('Error saving phone:', error);
      alert('Error al guardar el teléfono: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (phone) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEditingPhone(phone);
    setFormData({
      marca: phone.marca, modelo: phone.modelo, imei: phone.imei || '',
      serial: phone.serial || '', status: phone.status || '', user: phone.user || '',
      condicion: phone.condicion || '', comentarios: phone.comentarios || '',
      equipo_asociado: phone.equipo_asociado || '', ubicacion: phone.ubicacion || '',
      precio_base: phone.precio_base || '', estrellas: phone.estrellas || 3,
      grado: phone.grado || 'B', sistema_operativo: phone.sistema_operativo || 'Android',
      numero_activo: phone.numero_activo || '', vendido: phone.vendido || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este teléfono?')) {
      try {
        await phoneAPI.delete(id);
        alert('Teléfono eliminado exitosamente');
        loadPhones();
      } catch (error) {
        console.error('Error deleting phone:', error);
        alert('Error al eliminar el teléfono');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      marca: "",
      modelo: "",
      imei: "",
      serial: "",
      numero_activo: "",
      precio_base: "",
      grado: "B",
      sistema_operativo: "Android",
      condicion: "",
      comentarios: "",
      status: "",
      ubicacion: "",
      user: "",
      vendido: false
    });
    setEditingPhone(null);
    setShowForm(false);
  };

  const filteredPhones = phones.filter(phone => 
    phone.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    phone.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (phone.imei && phone.imei.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (phone.serial && phone.serial.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (phone.numero_activo && phone.numero_activo.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>⚙️ Panel de Administración</h1>
        <div className="admin-actions">
          <button onClick={() => navigate('/')} className="btn-catalog">📱 Ver Catálogo</button>
          <button onClick={() => navigate('/users')} className="btn-users">👥 Usuarios</button>
          <button onClick={() => navigate('/bids-report')} className="btn-bids-report">📊 Ofertas</button>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn-logout">🚪 Salir</button>
        </div>
      </header>
      <div className="admin-content">
        <div className="admin-toolbar">
          <button onClick={() => setShowForm(!showForm)} className="btn-add">
            {showForm ? '❌ Cancelar' : '➕ Agregar Teléfono'}
          </button>
          <div className="search-bar-admin">
            <input
              type="text"
              placeholder="🔍 Buscar por marca, modelo, IMEI o usuario...erial o # activo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {showForm && (
          <div className="phone-form-container">
            <h2>{editingPhone ? '✏️ Editar Teléfono' : '➕ Nuevo Teléfono'}</h2>
            <form onSubmit={handleSubmit} className="phone-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Marca *</label>
                  <input type="text" value={formData.marca} onChange={(e) => setFormData({...formData, marca: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Modelo *</label>
                  <input type="text" value={formData.modelo} onChange={(e) => setFormData({...formData, modelo: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Sistema Operativo *</label>
                  <select value={formData.sistema_operativo} onChange={(e) => setFormData({...formData, sistema_operativo: e.target.value})}>
                    <option value="Android">🤖 Android</option>
                    <option value="iOS">🍎 iOS (Apple)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Número de Activo</label>
                  <input type="text" value={formData.numero_activo} onChange={(e) => setFormData({...formData, numero_activo: e.target.value})} placeholder="Ej: ACT-2024-001" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>IMEI</label>
                  <input type="text" value={formData.imei} onChange={(e) => setFormData({...formData, imei: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Serial</label>
                  <input type="text" value={formData.serial} onChange={(e) => setFormData({...formData, serial: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Estado</label>
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                    <option value="">Seleccionar...</option>
                    <option value="En uso">En uso</option>
                    <option value="Dañado">Dañado</option>
                    <option value="Subasta">Subasta</option>
                    <option value="Excelente">Excelente</option>
                    <option value="En almacen">En almacén</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Usuario</label>
                  <input type="text" value={formData.user} onChange={(e) => setFormData({...formData, user: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Ubicación</label>
                  <input type="text" value={formData.ubicacion} onChange={(e) => setFormData({...formData, ubicacion: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Equipo Asociado</label>
                  <input type="text" value={formData.equipo_asociado} onChange={(e) => setFormData({...formData, equipo_asociado: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Precio Base (RD$)</label>
                  <input type="number" step="0.01" value={formData.precio_base} onChange={(e) => setFormData({...formData, precio_base: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Grado de Condición *</label>
                  <select value={formData.grado} onChange={(e) => setFormData({...formData, grado: e.target.value})}>
                    <option value="A">🟢 Grado A - Excelente</option>
                    <option value="B">🟡 Grado B - Bueno</option>
                    <option value="C">🔴 Grado C - Regular</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={formData.vendido} 
                      onChange={(e) => setFormData({...formData, vendido: e.target.checked})}
                    />
                    <span>✅ Marcar como VENDIDO / NO DISPONIBLE</span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Condición</label>
                <textarea value={formData.condicion} onChange={(e) => setFormData({...formData, condicion: e.target.value})} rows="3" />
              </div>
              <div className="form-group">
                <label>Comentarios</label>
                <textarea value={formData.comentarios} onChange={(e) => setFormData({...formData, comentarios: e.target.value})} rows="3" />
              </div>
              <div className="form-group">
                <label>📷 Agregar Fotos del Teléfono</label>
                <div className="photo-upload-section">
                  <label htmlFor="photo-input" className="btn-upload-photo">
                    📸 Tomar Foto / Seleccionar Imagen
                  </label>
                  <input
                    id="photo-input"
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handlePhotoCapture}
                  />
                  <p className="upload-hint">Puedes seleccionar múltiples fotos a la vez</p>
                </div>
                {selectedPhotos.length > 0 && (
                  <div className="selected-photos">
                    <p className="photo-count">📷 {selectedPhotos.length} foto(s) seleccionada(s)</p>
                    <div className="photo-preview-grid">
                      {selectedPhotos.map((photo, index) => (
                        <div key={index} className="photo-preview-item">
                          <img src={URL.createObjectURL(photo)} alt={`Foto ${index + 1}`} />
                          <button 
                            type="button" 
                            onClick={() => removePhoto(index)}
                            className="btn-remove-photo"
                          >
                            ❌
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="form-buttons">
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
                <button type="button" onClick={resetForm} className="btn-cancel">
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
        <div className="phones-table-container">
          <h2>📱 Teléfonos Registrados ({filteredPhones.length})</h2>
          <div className="table-responsive">
            <table className="phones-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>SO</th>
                  <th># Activo</th>
                  <th>IMEI</th>
                  <th>Estado</th>
                  <th>Ubicación</th>
                  <th>Precio</th>
                  <th>Grado</th>
                  <th>Vendido</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPhones.map(phone => (
                  <tr key={phone.id}>
                    <td><strong>#{phone.id}</strong></td>
                    <td>{phone.marca}</td>
                    <td>{phone.modelo}</td>
                    <td>{phone.sistema_operativo === 'iOS' ? '🍎' : '🤖'}</td>
                    <td>{phone.numero_activo || 'N/A'}</td>
                    <td>{phone.imei || 'N/A'}</td>
                    <td><span className={`status-badge ${phone.status}`}>{phone.status || 'N/A'}</span></td>
                    <td>{phone.ubicacion || 'N/A'}</td>
                    <td>{phone.precio_base ? `RD$ ${parseFloat(phone.precio_base).toLocaleString()}` : 'N/A'}</td>
                    <td><span className={`grado-badge grado-${phone.grado || 'B'}`}>{phone.grado || 'B'}</span></td>
                    <td>{phone.vendido ? '✅ SI' : '❌ NO'}</td>
                    <td className="action-buttons">
                      <button onClick={() => handleEdit(phone)} className="btn-edit">✏️</button>
                      <button onClick={() => handleDelete(phone.id)} className="btn-delete">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
